import { HttpError } from "@microbeta/common-frontend";
import {
  type CreateModelInput,
  CreateModelSchema,
  type Model,
  ModelSchema,
  type UpdateModelInput,
  UpdateModelSchema,
} from "@microbeta/common-types";
import * as z from "zod";

const GetSignedUrlResponseSchema = z.object({
  signedUrl: z.string(),
  path: z.string(),
  bucketName: z.string(),
});

import config from "../../../config";
import { httpClient } from "../../../shared/utils/httpClientInstance";

export type CreateModelPayload = CreateModelInput;
export type UpdateModelPayload = UpdateModelInput;
type GetSignedUrlResponse = z.infer<typeof GetSignedUrlResponseSchema>;

interface PaginatedModelsResponseRaw {
  models: unknown[];
  total: number;
  limit: number;
  offset: number;
}

// Add pagination response type
export interface PaginatedModelsResponse {
  models: Model[];
  total: number;
  limit: number;
  offset: number;
}

// --- Helper for handling Zod errors ---
const handleZodError = (error: z.ZodError, context: string): Error => {
  console.error(`${context} validation failed:`, error.issues);
  const errorMessages = error.issues
    .map((e) => `${e.path.join(".")} - ${e.message}`)
    .join(", ");
  return new Error(`Invalid data (${context}): ${errorMessages}`);
};

// biome-ignore lint/suspicious/noExplicitAny: legacy data structure
const normalizeLegacyImages = (data: any): any => {
  if (!data) return data;

  if (Array.isArray(data)) {
    return data.map(normalizeLegacyImages);
  }

  if (typeof data === "object" && data !== null) {
    // If it's a model-like object with an images array
    if (Array.isArray(data.images)) {
      return {
        ...data,
        images: data.images.map((img: unknown) => {
          if (typeof img === "string") {
            const uuid =
              typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : "00000000-0000-0000-0000-000000000000";
            return {
              id: uuid,
              original: { url: img },
            };
          }
          if (typeof img === "object" && img !== null && !img.id) {
            const uuid =
              typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : "00000000-0000-0000-0000-000000000000";
            return {
              ...img,
              id: uuid,
            };
          }
          return img;
        }),
      };
    }
    // Also handle pagination objects like PaginatedModelsResponseRaw
    if (Array.isArray(data.models)) {
      return {
        ...data,
        models: data.models.map(normalizeLegacyImages),
      };
    }
  }

  return data;
};

const BASE_URL = `${config.VITE_CONTENT_SERVICE_URL}/content`;

export const createModel = async (
  modelData: CreateModelPayload
): Promise<Model> => {
  const validatedInput = CreateModelSchema.safeParse(modelData);
  if (!validatedInput.success) {
    throw handleZodError(validatedInput.error, "create model input");
  }

  try {
    const result = await httpClient.post<unknown>(
      `${BASE_URL}/models`,
      validatedInput.data
    );

    const parsedResult = ModelSchema.safeParse(normalizeLegacyImages(result));
    if (!parsedResult.success) {
      throw handleZodError(parsedResult.error, "create model response");
    }
    return parsedResult.data;
  } catch (error: unknown) {
    console.error("Error creating model:", error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to create model: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred while creating the model.");
  }
};

export const createDraftmodel = async (payload: {
  area_id: string;
}): Promise<{ id: string; slug: string }> => {
  try {
    const result = await httpClient.post<{ id: string; slug: string }>(
      `${BASE_URL}/models/draft`,
      payload
    );
    return result;
  } catch (error: unknown) {
    console.error("Error creating draft model:", error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to create draft model: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(
      "An unknown error occurred while creating the draft model."
    );
  }
};

export const getModels = async (): Promise<Model[]> => {
  try {
    const result = await httpClient.get<unknown[]>(`${BASE_URL}/models`);

    if (Array.isArray(result)) {
      // result = result.map(transformImageVariants);
    }

    const modelsArraySchema = z.array(ModelSchema);
    const parsedResult = modelsArraySchema.safeParse(
      normalizeLegacyImages(result)
    );
    if (!parsedResult.success) {
      console.error(
        "Problematic data snippet:",
        JSON.stringify(result).substring(0, 500)
      );
      throw handleZodError(parsedResult.error, "fetch models response");
    }
    return parsedResult.data;
  } catch (error: unknown) {
    console.error("Error fetching models:", error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to fetch models: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred while fetching models.");
  }
};

export const getModelById = async (id: string): Promise<Model | null> => {
  try {
    const result = await httpClient.get<unknown>(`${BASE_URL}/models/${id}`);
    // result = transformImageVariants(result);

    const parsedResult = ModelSchema.safeParse(normalizeLegacyImages(result));
    if (!parsedResult.success) {
      throw handleZodError(parsedResult.error, "fetch model by ID response");
    }
    return parsedResult.data;
  } catch (error: unknown) {
    if (error instanceof HttpError && error.status === 404) {
      return null;
    }
    console.error(`Error fetching model ${id}:`, error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to fetch model: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred while fetching the model.");
  }
};

export const getModelBySlug = async (slug: string): Promise<Model | null> => {
  try {
    const result = await httpClient.get<unknown>(
      `${BASE_URL}/models/slug/${slug}`
    );
    // result = transformImageVariants(result);

    const parsedResult = ModelSchema.safeParse(normalizeLegacyImages(result));
    if (!parsedResult.success) {
      throw handleZodError(parsedResult.error, "fetch model by slug response");
    }
    return parsedResult.data;
  } catch (error: unknown) {
    console.error(`Error fetching model slug ${slug}:`, error);
    if (error instanceof HttpError) {
      if (error.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch model: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred while fetching the model.");
  }
};

export const getModelsByAreaId = async (areaId: string): Promise<Model[]> => {
  try {
    const result = await httpClient.get<unknown[]>(
      `${BASE_URL}/models?areaId=${areaId}`
    );

    if (Array.isArray(result)) {
      // result = result.map(transformImageVariants);
    }

    const modelsArraySchema = z.array(ModelSchema);
    const parsedResult = modelsArraySchema.safeParse(
      normalizeLegacyImages(result)
    );
    if (!parsedResult.success) {
      console.error(
        `model validation failed for area ${areaId}:`,
        parsedResult.error.issues
      );
      throw handleZodError(parsedResult.error, "fetch models by area response");
    }
    return parsedResult.data;
  } catch (error: unknown) {
    console.error(`Error fetching models for area ${areaId}:`, error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to fetch models for area: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(
      "An unknown error occurred while fetching models for area."
    );
  }
};

export const getModelsByMultipleAreaIds = async (
  areaIds: string[]
): Promise<Model[]> => {
  try {
    if (!areaIds.length) {
      return [];
    }

    // Make parallel requests for all area IDs
    const modelPromises = areaIds.map(async (areaId) => {
      try {
        return await getModelsByAreaId(areaId);
      } catch (error) {
        console.warn(`Failed to fetch models for area ${areaId}:`, error);
        return [];
      }
    });

    const modelArrays = await Promise.all(modelPromises);

    // Flatten the arrays and remove duplicates
    const allModels = modelArrays.flat();
    const uniqueModels = allModels.filter(
      (model, index, self) => index === self.findIndex((b) => b.id === model.id)
    );

    return uniqueModels;
  } catch (error: unknown) {
    console.error(`Error fetching models for multiple areas:`, error);
    if (error instanceof HttpError) {
      throw new Error(
        `Failed to fetch models for multiple areas: ${error.message}`
      );
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(
      "An unknown error occurred while fetching models for multiple areas."
    );
  }
};

export const updateModel = async (
  id: string,
  modelData: UpdateModelPayload
): Promise<Model> => {
  const validatedInput = UpdateModelSchema.safeParse(modelData);
  if (!validatedInput.success) {
    throw handleZodError(validatedInput.error, "update model input");
  }

  try {
    const result = await httpClient.put<unknown>(
      `${BASE_URL}/models/${id}`,
      validatedInput.data
    );
    // result = transformImageVariants(result);

    const parsedResult = ModelSchema.safeParse(normalizeLegacyImages(result));
    if (!parsedResult.success) {
      throw handleZodError(parsedResult.error, "update model response");
    }
    return parsedResult.data;
  } catch (error: unknown) {
    console.error(`Error updating model ${id}:`, error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to update model: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred while updating the model.");
  }
};

export const getModelUploadUrl = async (
  modelId: string,
  payload: {
    filename: string;
    contentType: string;
    extension: string;
  }
): Promise<GetSignedUrlResponse> => {
  try {
    const result = await httpClient.post<GetSignedUrlResponse>(
      `${BASE_URL}/models/${modelId}/upload-url`,
      payload
    );

    const parsedResult = GetSignedUrlResponseSchema.safeParse(result);
    if (!parsedResult.success) {
      throw handleZodError(parsedResult.error, "model upload URL response");
    }
    return parsedResult.data;
  } catch (error: unknown) {
    console.error(`Error getting model upload URL for ${modelId}:`, error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to get model upload URL: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred while getting the upload URL.");
  }
};

export const deleteModel = async (id: string): Promise<void> => {
  try {
    await httpClient.del(`${BASE_URL}/models/${id}`);
  } catch (error: unknown) {
    console.error(`Error deleting model ${id}:`, error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to delete model: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred while deleting the model.");
  }
};

// Updated function for admin dashboard to get all models with pagination
export const getAllmodels = async (
  limit = 50,
  offset = 0,
  summary = false
): Promise<PaginatedModelsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("limit", limit.toString());
    queryParams.append("offset", offset.toString());
    if (summary) {
      queryParams.append("summary", "true");
    }

    const url = `${BASE_URL}/models/all?${queryParams.toString()}`;
    const result = await httpClient.get<PaginatedModelsResponseRaw | unknown[]>(
      url
    );

    // Handle legacy response format
    if (Array.isArray(result)) {
      const areas = await httpClient.get<Array<{ id: string }>>(
        `${BASE_URL}/areas`
      );

      const modelPromises: Promise<Model[]>[] = [];

      // Add promises for area models
      if (Array.isArray(areas)) {
        for (const area of areas) {
          modelPromises.push(
            httpClient
              .get<unknown[]>(`${BASE_URL}/models?areaId=${area.id}`)
              .then((modelResult) => {
                const transformedResult = Array.isArray(modelResult)
                  ? modelResult
                  : modelResult;
                const parsedResult = z
                  .array(ModelSchema)
                  .safeParse(transformedResult);
                if (!parsedResult.success) {
                  console.warn(
                    `Failed to parse models for area ${area.id}:`,
                    parsedResult.error
                  );
                  return [];
                }
                return parsedResult.data;
              })
              .catch((error) => {
                console.warn(
                  `Failed to fetch models for area ${area.id}:`,
                  error
                );
                return [];
              })
          );
        }
      }

      // Wait for all model requests to complete
      const modelArrays = await Promise.all(modelPromises);

      // Flatten the arrays and remove duplicates
      const allModels = modelArrays.flat();
      const uniqueModels = allModels.filter(
        (model, index, self) =>
          index === self.findIndex((b) => b.id === model.id)
      );

      // Apply pagination manually for legacy format
      const start = offset;
      const end = start + limit;
      const paginatedModels = uniqueModels.slice(start, end);

      return {
        models: paginatedModels,
        total: uniqueModels.length,
        limit,
        offset,
      };
    }

    // Handle modern paginated response format
    const modelsData = result.models || [];
    const transformedModels = modelsData;
    const modelsArraySchema = z.array(ModelSchema);
    const parsedResult = modelsArraySchema.safeParse(transformedModels);

    if (!parsedResult.success) {
      console.error(
        "Problematic data snippet:",
        JSON.stringify(modelsData).substring(0, 500)
      );
      throw handleZodError(parsedResult.error, "fetch all models response");
    }

    return {
      models: parsedResult.data,
      total: result.total,
      limit: result.limit,
      offset: result.offset,
    };
  } catch (error: unknown) {
    console.error("Error fetching all models:", error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to fetch all models: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred while fetching all models.");
  }
};

export const getAllModelsAdmin = async (
  limit = 50,
  offset = 0,
  search = ""
): Promise<PaginatedModelsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("limit", limit.toString());
    queryParams.append("offset", offset.toString());
    if (search) {
      queryParams.append("search", search);
    }

    const url = `${BASE_URL}/models/admin?${queryParams.toString()}`;
    const result = await httpClient.get<PaginatedModelsResponseRaw>(url);

    const modelsData = result.models || [];
    const modelsArraySchema = z.array(ModelSchema);
    const parsedResult = modelsArraySchema.safeParse(modelsData);

    if (!parsedResult.success) {
      throw handleZodError(parsedResult.error, "fetch admin models response");
    }

    return {
      models: parsedResult.data,
      total: result.total,
      limit: result.limit,
      offset: result.offset,
    };
  } catch (error: unknown) {
    console.error("Error fetching admin models:", error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to fetch admin models: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred while fetching admin models.");
  }
};

export const deleteModelImage = async (
  modelId: string,
  imageUrl: string
): Promise<{ message: string; updatedImages: string[] }> => {
  try {
    const result = await httpClient.del<{
      message: string;
      updatedImages: string[];
    }>(`${BASE_URL}/models/${modelId}/images`, {
      body: JSON.stringify({ imageUrl }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return result;
  } catch (error: unknown) {
    console.error(`Error deleting model image for ${modelId}:`, error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to delete model image: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(
      "An unknown error occurred while deleting the model image."
    );
  }
};

export const addModelImage = async (
  modelId: string,
  imageUrl: string
): Promise<{ message: string; updatedImages: string[] }> => {
  try {
    const result = await httpClient.post<{
      message: string;
      updatedImages: string[];
    }>(`${BASE_URL}/models/${modelId}/images`, { imageUrl });
    return result;
  } catch (error: unknown) {
    console.error(`Error adding image to model ${modelId}:`, error);
    if (error instanceof HttpError) {
      throw new Error(`Failed to add image: ${error.message}`);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unknown error occurred while adding the image.");
  }
};
